// voicefx.js — the Konami-code cheat-menu voice effects. Each effect processes a mic
// track through a WebAudio graph and returns a replacement MediaStreamTrack; the
// victim's own client swaps it onto its WebRTC senders so EVERYONE hears the effect,
// while the raw mic keeps feeding the meeting-notes recorder untouched.
//
// Pitch effects (chipmunk/demon) run a tiny granular shifter in an AudioWorklet
// (audio thread — no main-thread throttling): two delay-line readers sweep against
// the write head at the pitch ratio, triangle-crossfaded so their gains always sum
// to 1 and the wrap-around jumps land on silent gain. The rest are plain node graphs.

export const VOICE_EFFECTS = [
  { id: "chipmunk", emoji: "🐿️", label: "Chipmunk" },
  { id: "demon", emoji: "👹", label: "Demon" },
  { id: "robot", emoji: "🤖", label: "Robot" },
  { id: "telephone", emoji: "☎️", label: "Old telephone" },
  { id: "cave", emoji: "🕳️", label: "Cave echo" },
  { id: "underwater", emoji: "🌊", label: "Underwater" },
  { id: "badconnection", emoji: "📶", label: "Bad connection — random dropouts (sounds like tech)" },
  { id: "echoback", emoji: "🔁", label: "Echo their own voice back — ONLY they hear it" },
];

// Video effects are receiver-side CSS (`.meet__tile--vfx-<id>` in styles.css) — every
// client gets the same MeetingVideoFx broadcast, so everyone renders the victim's tile
// with the same filter/transform. No canvas pipeline, works on every browser.
export const VIDEO_EFFECTS = [
  { id: "upsidedown", emoji: "🙃", label: "Upside down" },
  { id: "alien", emoji: "👽", label: "Alien" },
  { id: "oldfilm", emoji: "📼", label: "Old film" },
  { id: "nightvision", emoji: "🌚", label: "Night vision" },
  { id: "drunk", emoji: "🫠", label: "Drunk cam" },
  { id: "ghost", emoji: "👻", label: "Ghost" },
  { id: "fly", emoji: "🪰", label: "Fly on the screen" }, // FlyOverlay component, not a CSS filter
  { id: "hair", emoji: "🌀", label: "Pube on the screen" }, // HairOverlay — a short curly strand
  { id: "slowzoom", emoji: "🔍", label: "Slow creeping zoom (~4 min)" },
  { id: "tilt", emoji: "📐", label: "Slightly skew camera" },
];

const PROCESSOR = `
registerProcessor("pitchshift", class extends AudioWorkletProcessor {
  constructor(options) {
    super();
    this.rate = (options && options.processorOptions && options.processorOptions.rate) || 1.5;
    this.buf = new Float32Array(1 << 15); // ring buffer, ~0.68s @ 48kHz
    this.w = 0;
    this.phase = 0;
  }
  process(inputs, outputs) {
    const inp = inputs[0] && inputs[0][0];
    const out = outputs[0] && outputs[0][0];
    if (!out) return true;
    const buf = this.buf, N = buf.length;
    const L = Math.floor(sampleRate * 0.08); // grain sweep length (~80ms)
    for (let i = 0; i < out.length; i++) {
      buf[this.w] = inp ? inp[i] : 0;
      this.phase = ((this.phase + (this.rate - 1)) % L + L) % L; // works for rates < 1 too
      let sum = 0;
      for (const off of [0, L / 2]) {
        const d = L - ((this.phase + off) % L);  // delay sweep = reading at 'rate' × realtime
        const g = 1 - Math.abs((2 * d) / L - 1); // triangle window; the offset pair sums to 1
        const r = (this.w - d + N) % N;
        const j = Math.floor(r), f = r - j;
        sum += g * (buf[j] * (1 - f) + buf[(j + 1) % N] * f);
      }
      out[i] = sum;
      this.w = (this.w + 1) % N;
    }
    return true;
  }
});
`;

let moduleUrl = null;

async function pitchGraph(ctx, src, dest, rate) {
  if (!moduleUrl) moduleUrl = URL.createObjectURL(new Blob([PROCESSOR], { type: "application/javascript" }));
  await ctx.audioWorklet.addModule(moduleUrl);
  const node = new AudioWorkletNode(ctx, "pitchshift",
    { numberOfInputs: 1, numberOfOutputs: 1, outputChannelCount: [1], processorOptions: { rate } });
  src.connect(node);
  node.connect(dest);
}

// Gentle saturation curve for the telephone crunch.
function softClipCurve(amount) {
  const n = 1024, curve = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const x = (i / (n - 1)) * 2 - 1;
    curve[i] = Math.tanh(amount * x) / Math.tanh(amount);
  }
  return curve;
}

async function buildGraph(ctx, src, dest, effect) {
  switch (effect) {
    case "chipmunk": return pitchGraph(ctx, src, dest, 1.5);
    case "demon": return pitchGraph(ctx, src, dest, 0.65);
    case "robot": { // ring modulation — input × 50Hz sine (Dalek)
      const ring = ctx.createGain();
      ring.gain.value = 0;
      const osc = ctx.createOscillator();
      osc.type = "sine";
      osc.frequency.value = 50;
      osc.connect(ring.gain);
      osc.start();
      src.connect(ring);
      ring.connect(dest);
      return;
    }
    case "telephone": { // 300–3200Hz bandpass + light saturation
      const hp = ctx.createBiquadFilter(); hp.type = "highpass"; hp.frequency.value = 300;
      const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = 3200;
      const clip = ctx.createWaveShaper(); clip.curve = softClipCurve(6);
      const g = ctx.createGain(); g.gain.value = 1.3;
      src.connect(hp); hp.connect(lp); lp.connect(clip); clip.connect(g); g.connect(dest);
      return;
    }
    case "cave": { // dry + feedback delay
      const dry = ctx.createGain(); dry.gain.value = 0.8;
      const delay = ctx.createDelay(1); delay.delayTime.value = 0.28;
      const fb = ctx.createGain(); fb.gain.value = 0.45;
      const wet = ctx.createGain(); wet.gain.value = 0.5;
      src.connect(dry); dry.connect(dest);
      src.connect(delay); delay.connect(fb); fb.connect(delay); delay.connect(wet); wet.connect(dest);
      return;
    }
    case "underwater": { // heavy lowpass with a slow wobble
      const lp = ctx.createBiquadFilter(); lp.type = "lowpass"; lp.frequency.value = 380; lp.Q.value = 0.8;
      const g = ctx.createGain(); g.gain.value = 1.6;
      const wob = ctx.createOscillator(); wob.frequency.value = 0.7;
      const wobAmt = ctx.createGain(); wobAmt.gain.value = 120;
      wob.connect(wobAmt); wobAmt.connect(lp.frequency); wob.start();
      src.connect(lp); lp.connect(g); g.connect(dest);
      return;
    }
    case "badconnection": { // random sub-second dropouts on the OUTGOING audio — reads as network trouble
      const gate = ctx.createGain();
      gate.gain.value = 1;
      src.connect(gate); gate.connect(dest);
      const iv = setInterval(() => {
        const t = ctx.currentTime + Math.random() * 2;
        const dur = 0.25 + Math.random() * 0.7;
        gate.gain.setValueAtTime(1, t);
        gate.gain.setValueAtTime(0, t + 0.01);
        gate.gain.setValueAtTime(1, t + dur);
      }, 15000 + Math.random() * 15000);
      ctx.addEventListener("statechange", () => { if (ctx.state === "closed") clearInterval(iv); });
      return;
    }
    case "echoback": { // outgoing audio UNCHANGED — but the victim hears their own voice ~250ms late,
      // quietly, on their own speakers. Nobody else hears a thing (classic delayed-auditory-feedback).
      src.connect(dest); // clean pass-through to the meeting
      const delay = ctx.createDelay(1); delay.delayTime.value = 0.25;
      const g = ctx.createGain(); g.gain.value = 0.35;
      src.connect(delay); delay.connect(g); g.connect(ctx.destination);
      return;
    }
    default: throw new Error("unknown effect");
  }
}

/** Build the effect graph for a mic track → { ctx, track, effect }. Close ctx to tear down.
 *  Mute still works — a disabled mic track feeds silence into the graph. */
export async function createVoiceFx(micTrack, effect) {
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) throw new Error("WebAudio unavailable");
  const ctx = new AC();
  try {
    const src = ctx.createMediaStreamSource(new MediaStream([micTrack]));
    const dest = ctx.createMediaStreamDestination();
    await buildGraph(ctx, src, dest, effect);
    await ctx.resume().catch(() => { /* already running */ });
    return { ctx, track: dest.stream.getAudioTracks()[0], effect };
  } catch (err) {
    try { ctx.close(); } catch { /* noop */ }
    throw err;
  }
}

/** The /nudge buzzer — an homage to MSN's: two low, rattly buzz bursts. Synthesized
 *  (no audio asset): a ~90 Hz sawtooth chopped hard by a 35 Hz tremolo so it rattles
 *  rather than hums. Silently a no-op before the page's first user gesture (autoplay
 *  policy suspends fresh AudioContexts) — the screen shake still lands. */
export function playNudgeBuzz() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    if (ctx.state === "suspended") { ctx.close(); return; } // no gesture yet — shake only
    const t0 = ctx.currentTime;
    const osc = ctx.createOscillator();
    osc.type = "sawtooth";
    osc.frequency.value = 90;
    // Tremolo stage: base 0.5 ± 0.5 at 35 Hz → full on/off chop = the buzz.
    const trem = ctx.createGain();
    trem.gain.value = 0.5;
    const lfo = ctx.createOscillator();
    lfo.type = "square";
    lfo.frequency.value = 35;
    const lfoDepth = ctx.createGain();
    lfoDepth.gain.value = 0.5;
    lfo.connect(lfoDepth).connect(trem.gain);
    // Envelope stage: two bursts (bzzt … bzzt), silence in between and after.
    const env = ctx.createGain();
    env.gain.setValueAtTime(0.0001, t0);
    for (const s of [t0, t0 + 0.36]) {
      env.gain.setValueAtTime(0.14, s);
      env.gain.setValueAtTime(0.14, s + 0.24);
      env.gain.exponentialRampToValueAtTime(0.0001, s + 0.3);
    }
    osc.connect(trem).connect(env).connect(ctx.destination);
    osc.start(t0); lfo.start(t0);
    osc.stop(t0 + 0.72); lfo.stop(t0 + 0.72);
    osc.onended = () => { try { ctx.close(); } catch { /* noop */ } };
  } catch { /* no audio — the shake still lands */ }
}

/** Cheat-menu confirmation blip for whoever pulled the prank: a quick 8-bit
 *  square-wave arpeggio — rising when an effect goes ON, falling when cleared. */
export function playCheatChirp(on = true) {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "square";
    const notes = on ? [523, 659, 784, 1046] : [1046, 784, 659, 523]; // C5 E5 G5 C6
    const t0 = ctx.currentTime, step = 0.06;
    notes.forEach((f, i) => osc.frequency.setValueAtTime(f, t0 + i * step));
    const end = t0 + notes.length * step;
    gain.gain.setValueAtTime(0.12, t0);
    gain.gain.exponentialRampToValueAtTime(0.0001, end + 0.05);
    osc.connect(gain).connect(ctx.destination);
    osc.start(t0);
    osc.stop(end + 0.06);
    osc.onended = () => { try { ctx.close(); } catch { /* noop */ } };
  } catch { /* no audio — the effect still applied */ }
}
